import { createSelector } from 'reselect';
import {
  getValueStatements,
  getFutureStatements,
  getPastStatements,
  getHealthStatements,
  getDividendStatements,
  HealthStatements,
} from './getStatements';
import type {
  SelectedStatement,
  AnalysisCheck,
} from '@company/redux/interface';
import { Mark, SnowflakeIndices } from '@company/redux/interface';
import { CompanyStatements } from '@/constants/statementIds';
import { getAnalysis } from '@company/redux/selectors';

const createScore = (
  statement: SelectedStatement,
  hash?: string
): AnalysisCheck => {
  let mark: Mark;
  if (statement.value === 'Pass') {
    mark = 'PASS';
  } else {
    mark = 'FAIL';
  }

  return {
    mark,
    hash,
    title: statement.title,
    description: statement.formattedDescription,
  };
};

const valueIds = [
  CompanyStatements.valueStatement1,
  CompanyStatements.valueStatement2,
  CompanyStatements.valueStatement3,
  CompanyStatements.valueStatement4,
  CompanyStatements.valueStatement5,
  CompanyStatements.valueStatement6,
];
export const getValueChecks = createSelector([getValueStatements], (value) => {
  return valueIds.map((id) => createScore(value[id], id));
});

const valuationId = [
  CompanyStatements.valuationStatement1,
  CompanyStatements.valuationStatement2,
  CompanyStatements.valuationStatement3,
  CompanyStatements.valuationStatement4,
  CompanyStatements.valuationStatement5,
  CompanyStatements.valuationStatement7,
];

export const getValuationChecks = createSelector(
  [getValueStatements, getAnalysis],
  (value) => {
    return valuationId.map((id) => createScore(value[id], id));
  }
);

const futureIds = [
  CompanyStatements.futureStatement1,
  CompanyStatements.futureStatement2,
  CompanyStatements.futureStatement3,
  CompanyStatements.futureStatement4,
  CompanyStatements.futureStatement5,
  CompanyStatements.futureStatement6,
];

export const getFutureChecks = createSelector(
  [getFutureStatements],
  (future) => {
    return futureIds.map((id) => createScore(future[id], id));
  }
);

const pastStatementIds = [
  CompanyStatements.pastStatement5,
  CompanyStatements.pastStatement6,
  CompanyStatements.pastStatement1,
  CompanyStatements.pastStatement2,
  CompanyStatements.pastStatement3,
  CompanyStatements.pastStatement4,
];

export const getPastChecks = createSelector([getPastStatements], (past) => {
  return pastStatementIds.map((id) => createScore(past[id], id));
});

const healthIds = [
  CompanyStatements.healthStatement1,
  CompanyStatements.healthStatement2,
  CompanyStatements.healthStatement3,
  CompanyStatements.healthStatement4,
  CompanyStatements.healthStatement5,
  CompanyStatements.healthStatement6,
];

const alternateHealthIds = [
  CompanyStatements.alternativeHealthStatement1,
  CompanyStatements.alternativeHealthStatement2,
  CompanyStatements.alternativeHealthStatement3,
  CompanyStatements.alternativeHealthStatement4,
  CompanyStatements.alternativeHealthStatement5,
  CompanyStatements.alternativeHealthStatement6,
];

const lossMakingHealthIds = [
  CompanyStatements.lossMakingHealthStatement1,
  CompanyStatements.lossMakingHealthStatement2,
  CompanyStatements.lossMakingHealthStatement3,
  CompanyStatements.lossMakingHealthStatement4,
  CompanyStatements.lossMakingHealthStatement5,
  CompanyStatements.lossMakingHealthStatement6,
];

const healthStatementMapper =
  (health: HealthStatements) => (id: CompanyStatements) =>
    createScore(health[id], id);

export const getHealthCheck = createSelector(
  [getHealthStatements],
  (health) => {
    const mapStatement = healthStatementMapper(health);
    const isLossMaking =
      health.isProfitable.value === 'Fail' &&
      health.isProfitableLongTerm.value === 'Fail';

    if (health.isBank.value === 'Pass') {
      return alternateHealthIds.map(mapStatement);
    }
    if (isLossMaking) {
      return lossMakingHealthIds.map(mapStatement);
    }
    return healthIds.map(mapStatement);
  }
);

const dividendStatementIds = [
  CompanyStatements.dividendStatement1,
  CompanyStatements.dividendStatement2,
  CompanyStatements.dividendStatement3,
  CompanyStatements.dividendStatement4,
  CompanyStatements.dividendStatement5,
  CompanyStatements.dividendStatement7,
];

const dividendStatementIdsForBanks = [
  CompanyStatements.dividendStatement1,
  CompanyStatements.dividendStatement2,
  CompanyStatements.dividendStatement3,
  CompanyStatements.dividendStatement4,
  CompanyStatements.dividendStatement5,
  CompanyStatements.dividendStatement6,
];

export const getDividendCheck = createSelector(
  [getDividendStatements],
  (dividend) => {
    const isBank = dividend.isBank.value === 'Pass';
    const ids = isBank ? dividendStatementIdsForBanks : dividendStatementIds;
    return ids.map((id) => createScore(dividend[id], id));
  }
);

const marker = (check: AnalysisCheck) => check.mark;

export const getLegacyAnalysisChecks = createSelector(
  [
    getValueChecks,
    getFutureChecks,
    getPastChecks,
    getHealthCheck,
    getDividendCheck,
  ],
  (value, future, past, health, dividend) => {
    return {
      [SnowflakeIndices.VALUE]: value.map(marker),
      [SnowflakeIndices.FUTURE]: future.map(marker),
      [SnowflakeIndices.PAST]: past.map(marker),
      [SnowflakeIndices.HEALTH]: health.map(marker),
      [SnowflakeIndices.DIVIDEND]: dividend.map(marker),
    };
  }
);

export const getAnalysisChecks = createSelector(
  [
    getValuationChecks,
    getFutureChecks,
    getPastChecks,
    getHealthCheck,
    getDividendCheck,
  ],
  (value, future, past, health, dividend) => {
    return {
      [SnowflakeIndices.VALUE]: value.map(marker),
      [SnowflakeIndices.FUTURE]: future.map(marker),
      [SnowflakeIndices.PAST]: past.map(marker),
      [SnowflakeIndices.HEALTH]: health.map(marker),
      [SnowflakeIndices.DIVIDEND]: dividend.map(marker),
    };
  }
);
